import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SubmissionCard from "@/components/SubmissionCard";
import SubmissionDrawer from "@/components/SubmissionDrawer";
import StatsGrid from "@/components/StatsGrid";
import { Plus, Package, UserPlus, Users as UsersIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function AgencyDashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [subs, setSubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteFor, setInviteFor] = useState(null);
  const [form, setForm] = useState({ name: "", brand_id: "", description: "" });

  const load = useCallback(async () => {
    try {
      const [c, s, u, st] = await Promise.all([
        api.get("/campaigns"),
        api.get("/submissions"),
        api.get("/users"),
        api.get("/stats"),
      ]);
      setCampaigns(c.data);
      setSubs(s.data);
      setUsers(u.data);
      setStats(st.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const brands = users.filter((u) => u.role === "brand");
  const influencers = users.filter((u) => u.role === "influencer");
  const filteredSubs = filter === "all" ? subs : subs.filter((s) => s.status === filter);

  const createCampaign = async () => {
    if (!form.name.trim() || !form.brand_id) return toast.error("Name and brand required");
    try {
      await api.post("/campaigns", form);
      toast.success("Campaign created");
      setForm({ name: "", brand_id: "", description: "" });
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const inviteInfluencer = async (campaignId, influencerId) => {
    try {
      await api.post(`/campaigns/${campaignId}/invite`, { campaign_id: campaignId, influencer_id: influencerId });
      toast.success("Influencer added to campaign");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="animate-fade-in-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-black text-4xl">Agency Overview</h1>
          <p className="text-zinc-400 mt-1">Mastra Media — the command center.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-campaign-button" className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-full px-5 h-11">
              <Plus size={18} weight="bold" className="mr-1" /> New campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-black border-white/10 text-zinc-50 sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-black text-2xl">New campaign</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-zinc-300">Campaign name</Label>
                <Input data-testid="campaign-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-white/10 mt-1.5 h-11" placeholder="Summer Drop 2026" />
              </div>
              <div>
                <Label className="text-zinc-300">Brand</Label>
                <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                  <SelectTrigger data-testid="campaign-brand-select" className="bg-zinc-900 border-white/10 mt-1.5 h-11">
                    <SelectValue placeholder="Choose brand" />
                  </SelectTrigger>
                  <SelectContent className="bg-black border-white/10 text-zinc-100">
                    {brands.length ? brands.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name} · {b.company || b.email}</SelectItem>
                    )) : <div className="p-3 text-sm text-zinc-500">No brands registered</div>}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Brief</Label>
                <Textarea data-testid="campaign-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-white/10 mt-1.5 min-h-24" placeholder="Campaign details, deliverables…" />
              </div>
              <Button onClick={createCampaign} data-testid="create-campaign-submit" className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11">Create campaign</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <StatsGrid stats={stats} role="agency_admin" />

      {/* Campaigns */}
      <section className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl flex items-center gap-2">
            <Package size={22} weight="duotone" className="text-yellow-400" /> Campaigns
          </h2>
        </div>
        {campaigns.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-zinc-500 text-sm">
            No campaigns yet — create your first campaign above.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {campaigns.map((c) => (
              <div key={c.id} data-testid={`campaign-card-${c.id}`} className="rounded-xl border border-white/10 bg-zinc-900/60 p-5 card-hover">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-lg truncate">{c.name}</div>
                    <div className="text-xs text-zinc-500 font-mono truncate">{c.brand_name}</div>
                  </div>
                </div>
                {c.description && <p className="text-sm text-zinc-400 line-clamp-2 mb-3">{c.description}</p>}
                <div className="flex items-center justify-between text-xs text-zinc-500 mb-3">
                  <span><UsersIcon size={14} className="inline mr-1" />{c.influencer_ids?.length || 0} influencers</span>
                </div>
                <Button
                  onClick={() => setInviteFor(c)}
                  data-testid={`invite-influencer-button-${c.id}`}
                  variant="outline"
                  size="sm"
                  className="w-full border-white/10 bg-transparent hover:bg-white/5 text-zinc-200 h-9"
                >
                  <UserPlus size={14} className="mr-1.5" /> Add influencer
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Submissions */}
      <section className="mt-10">
        <h2 className="font-heading font-bold text-xl mb-4">All submissions</h2>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-zinc-900 border border-white/10">
            <TabsTrigger value="all" data-testid="agency-filter-all">All</TabsTrigger>
            <TabsTrigger value="pending" data-testid="agency-filter-pending">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="agency-filter-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="agency-filter-rejected">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {filteredSubs.map((s) => (
            <SubmissionCard key={s.id} submission={s} onClick={() => setActiveId(s.id)} />
          ))}
          {filteredSubs.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed border-white/10 p-12 text-center text-zinc-500 text-sm">
              No submissions in this filter.
            </div>
          )}
        </div>
      </section>

      <SubmissionDrawer
        submissionId={activeId}
        open={!!activeId}
        onClose={() => setActiveId(null)}
        role="agency_admin"
        onUpdated={load}
      />

      {/* Invite influencer dialog */}
      <Dialog open={!!inviteFor} onOpenChange={(v) => !v && setInviteFor(null)}>
        <DialogContent className="bg-black border-white/10 text-zinc-50 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl">Add influencer to {inviteFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto mt-2 space-y-2">
            {influencers.length === 0 && <p className="text-sm text-zinc-500">No influencers registered.</p>}
            {influencers.map((i) => {
              const already = inviteFor?.influencer_ids?.includes(i.id);
              return (
                <div key={i.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-zinc-900/60 p-3">
                  <div>
                    <div className="text-sm font-medium">{i.name}</div>
                    <div className="text-xs text-zinc-500">{i.email}</div>
                  </div>
                  <Button
                    disabled={already}
                    onClick={() => inviteInfluencer(inviteFor.id, i.id)}
                    data-testid={`add-influencer-${i.id}`}
                    size="sm"
                    className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-8"
                  >
                    {already ? "Added" : "Add"}
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
